/**
 * Helios Brain — Lab data layer.
 *
 * Centralised data for the "serious research lab" branding moves:
 *   - PAPERS: named institutional artifacts (vs generic white papers)
 *   - PROJECTS: codenamed live research initiatives (Greek mythology)
 *   - LAB_NOTES: date-stamped "from the lab" feed
 *   - MISSION_AREAS: content categorisation by research area (not by type)
 */

export const MISSION_AREAS = [
  { slug: "substrate",   label: "Substrate",   accent: "#FAFAFA" },
  { slug: "governance",  label: "Governance",  accent: "#FAFAFA" },
  { slug: "causality",   label: "Causality",   accent: "#FAFAFA" },
  { slug: "reasoning",   label: "Reasoning",   accent: "#FAFAFA" },
  { slug: "society",     label: "Society",     accent: "#FAFAFA" },
  { slug: "athens",      label: "Athens Notes",accent: "#FAFAFA" },
];

/* ────────────────────────────────────────────────────────────
   NAMED INSTITUTIONAL ARTIFACTS. These are not "blog posts".
   Each one is a branded document with its own identity.
   ──────────────────────────────────────────────────────────── */
export const PAPERS = [
  {
    code: "HB-PP-2026-01",
    slug: "locus-of-competence",
    title: "The Locus of Competence: Composable, Governed Intelligence and the Architecture of Defensible Decisions",
    kind: "Position Paper",
    area: "reasoning",
    date: "June 2026",
    pages: 14,
    abstract:
      "Machine competence does not have to live inside a model's parameters. A capable system can be split into a general policy that decides what to do and a library of specialized, inspectable, executable components that do it. For decisions that carry institutional weight, the difficulty is not running the right component but composing several without losing what each one guarantees. The trustworthiness of a composed decision is bounded by the weakest condition it violates anywhere in the pipeline; learning from outcomes has to preserve auditability to be admissible at all; and the right test of such a system is whether it can close a single measured, governed decision loop.",
    tags: ["Composition", "Governance", "Causality", "Calibration"],
    status: "Position Paper",
  },
  {
    code: "HB-TR-2026-05",
    slug: "memory-substrate",
    title: "The Living World Model: A Bitemporal Evidence Substrate",
    kind: "Technical Report",
    area: "substrate",
    date: "May 2026",
    pages: 32,
    abstract:
      "A bitemporal evidence substrate makes a world model 'living'. Each fact carries valid time (when it was true) and transaction time (when we believed it). An append-only ledger separates source of truth from a derived memory index. A continuous-learning loop updates calibration via proper scoring rules.",
    tags: ["Memory", "Bitemporal", "Provenance", "Learning"],
    status: "Technical Report",
  },
  {
    code: "HB-TR-2026-04",
    slug: "neurosymbolic-smt",
    title: "Certifiable Safety and Semantic Interoperability via Neurosymbolic Ontology and SMT",
    kind: "Technical Report",
    area: "substrate",
    date: "April 2026",
    pages: 28,
    abstract:
      "High-risk environments need ontologies that are typed, axiomatised, and checkable. We use language models to propose and SMT solvers to dispose: schema generation, semantic bridge axioms, and safety properties become satisfiability problems with proof certificates or concrete counterexamples.",
    tags: ["Neurosymbolic", "SMT", "Ontology", "Safety"],
    status: "Technical Report",
  },
  {
    code: "HB-TR-2026-03",
    slug: "differentiable-abm",
    title: "Differentiable Agent-Based World Models for Counterfactual Policy Analysis",
    kind: "Technical Report",
    area: "causality",
    date: "March 2026",
    pages: 26,
    abstract:
      "Discrete agent choices block gradient flow in classical agent-based models. We relax via Gumbel-softmax, calibrate by simulated minimum distance, define interventions as program transformations, and treat counterfactuals as fixed-noise replay. A synthetic P&C claims environment demonstrates fraud detection and policy evaluation.",
    tags: ["ABM", "Causality", "Gumbel-softmax", "Counterfactual"],
    status: "Technical Report",
  },
  {
    code: "HB-TR-2026-02",
    slug: "themis-governance",
    title: "ΘΕΜΙΣ: A Cryptographically Verifiable Governance Ledger for Auditable Decision Systems",
    kind: "Technical Report",
    area: "governance",
    date: "February 2026",
    pages: 24,
    abstract:
      "ΘΕΜΙΣ interposes a verification gate and an append-only, hash-linked, Ed25519-signed ledger between decision engines and consequential actions. A neurosymbolic firewall checks policies via SMT. A PII gateway enforces egress rules. Four security properties (tamper-evidence, non-repudiation, append-only integrity, audit completeness) are formalised and proven.",
    tags: ["Governance", "Cryptography", "SMT", "Audit"],
    status: "Technical Report",
  },
  {
    code: "HB-001",
    slug: "athens-position",
    title: "The Athens Position",
    kind: "Manifesto",
    area: "athens",
    date: "February 2026",
    pages: 18,
    abstract:
      "On why governed, living world models, built inside European institutions, are a better foundation for organisational intelligence than autoregressive assistants alone. Five claims, defended.",
    tags: ["Manifesto", "Position", "Europe"],
    status: "Read online",
  },
  {
    code: "HB-002",
    slug: "substrate-spec",
    title: "Helios Substrate · Specification v1",
    kind: "Technical Specification",
    area: "substrate",
    date: "February 2026",
    pages: 42,
    abstract:
      "The formal specification of the Helios substrate: typed ontology, bitemporal evidence, provenance contracts, and the closed-loop learning protocol that connects them. Implementation-independent.",
    tags: ["Specification", "Ontology", "Provenance"],
    status: "Draft v1",
  },
  {
    code: "HB-003",
    slug: "refusal-calculus",
    title: "The Refusal Calculus",
    kind: "Research Preview",
    area: "reasoning",
    date: "January 2026",
    pages: 24,
    abstract:
      "When should a decision system refuse to answer? We propose a calculus of provable refusal: certify that a query lies outside the model's evidence, and return a citable bound rather than a hedged extrapolation.",
    tags: ["Reasoning", "Calibration", "Safety"],
    status: "Preprint",
  },
  {
    code: "HB-004",
    slug: "living-intelligence-principles",
    title: "Living Intelligence · Principles",
    kind: "Constitution",
    area: "governance",
    date: "December 2025",
    pages: 12,
    abstract:
      "The seven principles by which a Helios world model is built, governed, and held accountable. Our equivalent of a constitution: short enough to enforce, long enough to mean something.",
    tags: ["Principles", "Governance", "Accountability"],
    status: "Public",
  },
  {
    code: "HB-005",
    slug: "eu-ai-act-compliance",
    title: "EU AI Act · Compliance Framework",
    kind: "Governance Document",
    area: "governance",
    date: "November 2025",
    pages: 36,
    abstract:
      "How a Helios world model maps to the obligations of the EU AI Act across risk classification, transparency, human oversight, robustness, and post-market monitoring. Auditor-ready.",
    tags: ["EU AI Act", "DORA", "GDPR Art. 22"],
    status: "Reference",
  },
];

/* ────────────────────────────────────────────────────────────
   PROJECT CODENAMES. Live research initiatives.
   Names from Greek mythology, mapped to mission area.
   ──────────────────────────────────────────────────────────── */
export const PROJECTS = [
  {
    code: "P-AT",
    name: "Project Athena",
    area: "governance",
    domain: "Governance · Wisdom",
    motto: "Wise systems refuse before they harm.",
    description:
      "Adversarial governance for the substrate. Multi-agent red teams that probe rule boundaries, jailbreak permissions, exploit ontology gaps, and trace data exfiltration paths.",
    leads: ["Causality", "Reasoning", "Safety"],
    status: "Active",
  },
  {
    code: "P-DM",
    name: "Project Demeter",
    area: "causality",
    domain: "Causality · Harvest",
    motto: "Effects, not correlations.",
    description:
      "Practical causal identification in high-dimensional, partially confounded action spaces. Partial identification, sensitivity-bounded effects, disclosure-aware adjustment.",
    leads: ["Identification", "Sensitivity", "Disclosure"],
    status: "Active",
  },
  {
    code: "P-HP",
    name: "Project Hephaestus",
    area: "substrate",
    domain: "Engineering · Forge",
    motto: "Compile the world as a typed graph.",
    description:
      "The factory itself. Ontology compiler, data wiring, reasoning composition, deployment runtime. The infrastructure on which every other project runs.",
    leads: ["Compiler", "Runtime", "Provenance"],
    status: "Active",
  },
  {
    code: "P-AP",
    name: "Project Apollo",
    area: "reasoning",
    domain: "Reasoning · Light",
    motto: "Many shapes of question, one substrate.",
    description:
      "Reasoning composition across eight layers: logical, combinatorial, continuous, probabilistic, causal, behavioural, temporal, governance. The right method for each shape of question.",
    leads: ["Composition", "Layers", "Type signatures"],
    status: "Active",
  },
  {
    code: "P-HS",
    name: "Project Hestia",
    area: "substrate",
    domain: "Memory · Hearth",
    motto: "What was known, when, and by whom.",
    description:
      "Bitemporal memory for the substrate. Every fact carries when it was true in the world, and when we learned about it. Audit survives leadership changes.",
    leads: ["Bitemporal", "Provenance", "Audit"],
    status: "Active",
  },
  {
    code: "P-HR",
    name: "Project Hermes",
    area: "reasoning",
    domain: "Interfaces · Messenger",
    motto: "Tacit expertise becomes governed evidence.",
    description:
      "The interface between domain experts and the substrate. Versioned, citable, governable tacit knowledge that does not flatten into brittle rules.",
    leads: ["Expert capture", "Versioning", "Citability"],
    status: "Exploratory",
  },
];

/* ────────────────────────────────────────────────────────────
   LAB NOTES. Date-stamped feed (DeepMind-style chronology).
   Each note is a short editorial entry from a mission area.
   ──────────────────────────────────────────────────────────── */
export const LAB_NOTES = [
  {
    date: "Feb 18, 2026",
    iso: "2026-02-18",
    area: "substrate",
    project: "Project Hephaestus",
    title: "Shipped: versioned ontology compilation",
    body:
      "Ontologies now compile to a typed graph with version pins on every entity-relation. Reasoning runs always cite the schema version. Customers can roll a substrate back to any prior schema and replay decisions deterministically.",
  },
  {
    date: "Feb 14, 2026",
    iso: "2026-02-14",
    area: "causality",
    project: "Project Demeter",
    title: "First write-up: partial-ontology uncertainty",
    body:
      "Internal preprint. We argue partial-ontology uncertainty is a first-class quantity, distinguishable from aleatoric and epistemic uncertainty over data, and composable across all eight reasoning layers without leakage.",
  },
  {
    date: "Feb 06, 2026",
    iso: "2026-02-06",
    area: "governance",
    project: "Project Athena",
    title: "Adversarial governance: first red-team report",
    body:
      "We ran a six-week adversarial campaign against the governance layer of a banking substrate. The team probed ontology gaps, permission boundaries, and policy staleness. Eighteen findings, all reproducible. Mitigations land in v1.6.",
  },
  {
    date: "Jan 28, 2026",
    iso: "2026-01-28",
    area: "reasoning",
    project: "Project Apollo",
    title: "Composing proofs with posteriors",
    body:
      "We are formalising how a recommendation that rests on a logically provable regulatory constraint and a probabilistic posterior over outcomes can compose without losing the type signature of each guarantee.",
  },
  {
    date: "Jan 22, 2026",
    iso: "2026-01-22",
    area: "athens",
    project: "Helios Lab",
    title: "Athens, January",
    body:
      "Three new hires from European universities. A first quarter of joint engineering with a sovereign wealth fund and a tier-one bank. Long evenings, short emails. The work feels right.",
  },
  {
    date: "Jan 14, 2026",
    iso: "2026-01-14",
    area: "society",
    project: "Helios Lab",
    title: "On useful intelligence",
    body:
      "A short note on why our lab focuses on organisational intelligence rather than autoregressive assistants. The world does not need a smarter answerer. It needs a substrate that remembers, governs, and learns from outcomes.",
  },
  {
    date: "Jan 02, 2026",
    iso: "2026-01-02",
    area: "substrate",
    project: "Project Hestia",
    title: "Bitemporal memory in production",
    body:
      "Helios Brain now records every fact with two timestamps: when it was true in the world, and when we learned about it. Audits can replay any prior state. Bitemporal is no longer a research property, it is a customer guarantee.",
  },
];

/* ────────────────────────────────────────────────────────────
   FOUNDER NOTES / PODCAST EPISODES. Placeholder catalogue.
   ──────────────────────────────────────────────────────────── */
export const NOTES_EPISODES = [
  {
    n: "01",
    title: "Why world models",
    duration: "08:42",
    date: "Feb 2026",
    summary: "On the difference between an assistant that answers and a substrate that models. Why one is a brochure and the other is infrastructure.",
  },
  {
    n: "02",
    title: "Why Athens",
    duration: "11:15",
    date: "Feb 2026",
    summary: "A European AI lab cannot be a copy of an American one. The case for building from a city that has thought about wisdom for two and a half thousand years.",
  },
  {
    n: "03",
    title: "EU AI Act as opportunity",
    duration: "13:08",
    date: "Jan 2026",
    summary: "Regulation as a moat for serious work. How risk classification, transparency obligations, and post-market monitoring become design constraints we welcome.",
  },
  {
    n: "04",
    title: "What governance means",
    duration: "09:33",
    date: "Jan 2026",
    summary: "Governance is not a layer on top of the model. It is the model. Permissions, provenance, refusal calculus, and adversarial review are the substrate, not an accessory.",
  },
  {
    n: "05",
    title: "A letter to the institutions",
    duration: "07:21",
    date: "Dec 2025",
    summary: "A direct address to the people who run banks, sovereign funds, ministries, hospitals, and utilities. What we are offering, and what we are asking in return.",
  },
];

/* ────────────────────────────────────────────────────────────
   TRUST + COMPLIANCE. The /trust hub data.
   ──────────────────────────────────────────────────────────── */
export const TRUST_SECTIONS = [
  {
    code: "01",
    title: "Data residency",
    body:
      "All Helios substrates run inside European data perimeters. Customer data does not leave the jurisdiction of the deploying institution. No model weights are exported. No prompts are mined.",
  },
  {
    code: "02",
    title: "EU AI Act · Risk classification",
    body:
      "Helios world models deployed for institutional decisions are designed against the obligations of a high-risk AI system under the EU AI Act: risk management, data governance, technical documentation, transparency, human oversight, robustness, and post-market monitoring.",
  },
  {
    code: "03",
    title: "GDPR Article 22 · Auditability",
    body:
      "Every recommendation is traceable to the data, evidence, ontology version, and reasoning composition that produced it. No purely automated decision is made without explainable rationale at the level the regulator and the data subject require.",
  },
  {
    code: "04",
    title: "DORA compatibility",
    body:
      "For financial sector clients, the substrate maps to DORA obligations across ICT risk management, incident reporting, resilience testing, and third-party risk. Audit artefacts are generated as a property of the runtime.",
  },
  {
    code: "05",
    title: "Provenance contracts",
    body:
      "Every signal binds to its source. Every fact carries when it was true and when we learned it. Every decision carries the evidence and policy that justified it. Provenance is a property of the substrate, not a dashboard feature.",
  },
  {
    code: "06",
    title: "Responsible disclosure",
    body:
      "Security researchers, auditors, and customers who discover a vulnerability are asked to contact hello@heliosbrain.com. We acknowledge within 48 hours and credit on disclosure unless requested otherwise.",
  },
];
